// @ts-nocheck
/*
 * Port of views/dashboard/contact-group/import-members.js (origin/main-dev, 268 lines).
 * BYTE-IDENTICAL — filters the current group ID out of the picker list,
 * validates ≥1 group selected + rejects when all selected groups have
 * zero members. Chrome swap: PrimeReact DataTable → obd3-gui DataTable;
 * PrimeReact Checkbox → shadcn.
 */
import React, { useState, useEffect, useRef } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSearch } from 'react-icons/fi';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_CONTACT_GROUP_MEMBER_UPLOAD,
  API_CONTACT_GROUP_LIST,
  API_CONTACT_GROUP_MEMBERS_UNDER_ONE_GROUP,
} from '@/service/url';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { toast } from 'sonner';
import { canImportContactGroupMembers } from '@/utilities/promptUploadAccess';
import { UploadNotPermitted } from '@/components/feedback/UploadNotPermitted';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';

// Must match MAX_PICKED_MEMBERS in contact-group-controller.uploadMembers.
const MAX_PICKED = 5000;

// Picks contacts of one group, one server page at a time; picks survive paging/search.
function MemberPickerDialog({ group, initial, onClose, onSave }) {
  const [picked, setPicked] = useState(() => new Set(initial));
  const [search, setSearch] = usePersistedState(`contact-group/ContactImportMembers.picker:${group.id}:search`, '');
  const [query, setQuery] = useState(() => search.trim());
  const [paging, setPaging] = usePersistedState(`contact-group/ContactImportMembers.picker:${group.id}:paging`, { pageIndex: 0, pageSize: 25 });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalCapped, setTotalCapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqSeq = useRef(0);
  // Keyset cursors for the current search + page size: pageIndex -> last id of the page before.
  // Unknown page (a jump) falls back to offset. The total is fetched once per search.
  const cursors = useRef({ key: '', map: new Map(), hasTotal: false });

  useEffect(() => {
    const t = setTimeout(() => {
      // Unchanged (e.g. restored) search keeps the restored page.
      if (search.trim() === query) return;
      setQuery(search.trim());
      setPaging((p) => (p.pageIndex ? { ...p, pageIndex: 0 } : p));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const seq = ++reqSeq.current;
    setLoading(true);
    const key = `${group.id}|${query}|${paging.pageSize}`;
    if (cursors.current.key !== key) cursors.current = { key, map: new Map(), hasTotal: false };
    const c = cursors.current;
    const params = new URLSearchParams({
      page: String(paging.pageIndex + 1),
      limit: String(paging.pageSize),
      search: query,
    });
    const after = c.map.get(paging.pageIndex);
    if (after) params.set('after', String(after));
    if (c.hasTotal) params.set('count', '0');
    const url = `${API_CONTACT_GROUP_MEMBERS_UNDER_ONE_GROUP.replace(':id', group.id)}?${params}`;
    getApiCall(url, {})
      .then((res) => {
        if (seq !== reqSeq.current) return;
        const list = res?.rows || [];
        setRows(list);
        if (list.length) c.map.set(paging.pageIndex + 1, list[list.length - 1].id);
        if (res?.total != null) {
          setTotal(Number(res.total) || 0);
          setTotalCapped(Boolean(res.total_capped));
          c.hasTotal = true;
        }
      })
      .catch(() => {
        if (seq === reqSeq.current) setRows([]);
      })
      .finally(() => {
        if (seq === reqSeq.current) setLoading(false);
      });
  }, [group.id, query, paging]);

  const pageIds = rows.map((r) => r.id);
  const pickedOnPage = pageIds.filter((mid) => picked.has(mid)).length;
  const toggle = (mid) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid);
      else next.add(mid);
      return next;
    });
  const togglePage = () =>
    setPicked((prev) => {
      const next = new Set(prev);
      const all = pageIds.every((mid) => next.has(mid));
      pageIds.forEach((mid) => (all ? next.delete(mid) : next.add(mid)));
      return next;
    });

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      enableHiding: false,
      header: () => (
        <Checkbox
          checked={
            pickedOnPage > 0 && pickedOnPage === pageIds.length
              ? true
              : pickedOnPage > 0
                ? 'indeterminate'
                : false
          }
          onCheckedChange={togglePage}
          disabled={!pageIds.length}
          aria-label="Select all on this page"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={picked.has(row.original.id)}
          onCheckedChange={() => toggle(row.original.id)}
          aria-label="Select contact"
        />
      ),
    },
    {
      id: 'name',
      header: 'Name',
      enableSorting: false,
      accessorFn: (r) => [r.first_name, r.last_name].filter(Boolean).join(' '),
    },
    { accessorKey: 'primary_number', header: 'Number', enableSorting: false },
    { accessorKey: 'company', header: 'Company', enableSorting: false },
  ];

  const tooMany = picked.size > MAX_PICKED;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Select contacts from {group.group_name}</DialogTitle>
          <DialogDescription>
            Your selection is kept across pages and searches. Numbers already in this group are skipped.
          </DialogDescription>
        </DialogHeader>
        <DataTable
          tableId="contact-group.import.picker"
          columns={columns}
          data={rows}
          isLoading={loading}
          pageSize={25}
          serverPagination={{ ...paging, rowCount: total, onChange: setPaging }}
          getRowId={(r) => String(r.id)}
          leftContent={
            <Input
              className="h-8 w-64"
              placeholder="Search name or number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          }
          emptyTitle={query ? 'No contacts match your search' : 'No contacts in this group'}
          disableExport
        />
        {totalCapped && (
          <p className="text-xs text-muted-foreground">
            More than {total.toLocaleString()} contacts match. Search by name or number to find the rest.
          </p>
        )}
        <DialogFooter className="items-center gap-2 sm:justify-between">
          <span className={tooMany ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
            {picked.size} selected{tooMany ? ` (max ${MAX_PICKED}, import the whole group instead)` : ''}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setPicked(new Set())} disabled={!picked.size}>
              Clear
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => onSave([...picked])} disabled={!picked.size || tooMany}>
              Use {picked.size} contact{picked.size === 1 ? '' : 's'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ContactGroupImport = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { checkAuth } = useAuth();
  const [searchValue, setSearchValue] = usePersistedState(`contact-group/ContactImportMembers:${id}:searchValue`, '');
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [groupsData, setGroupsData] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  // groupId -> picked member ids; a selected group with no entry is imported whole.
  const [picks, setPicks] = useState({});
  const [pickerGroup, setPickerGroup] = useState(null);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  useEffect(() => {
    getApiCall(API_CONTACT_GROUP_LIST, {}).then(onListSuccess).catch(onError);
  }, []);

  function onListSuccess(resp) {
    if (resp?.length > 0) {
      resp = resp.filter((item) => String(item.id) !== String(id));
    }
    setGroupsData(resp);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const filteredGroups = groupsData.filter((group) =>
    group.group_name.toLowerCase().includes(searchValue.toLowerCase()),
  );

  const handleImportMembers = () => {
    if (selectedGroups.length < 1) {
      toast.error('Please Select At least 1 Group');
      return;
    }

    const selectedGroupsData = selectedGroups
      .map((groupId) => groupsData.find((group) => group.id === groupId))
      .filter((group) => group !== undefined);

    const allGroupsEmpty = selectedGroupsData.every(
      (group) => group.member_count === 0 || !group.member_count,
    );

    if (allGroupsEmpty && selectedGroupsData.length > 0) {
      toast.error('All selected groups contain 0 members');
      return;
    }

    const groupIds = selectedGroups.filter((gid) => !picks[gid]);
    const selections = selectedGroups
      .filter((gid) => picks[gid])
      .map((gid) => ({ groupId: gid, memberIds: picks[gid] }));
    const pickedCount = selections.reduce((n, s) => n + s.memberIds.length, 0);
    if (pickedCount > MAX_PICKED) {
      toast.error(`Select at most ${MAX_PICKED} contacts, or import the whole group.`);
      return;
    }

    setButtonDisable(true);
    const url = API_CONTACT_GROUP_MEMBER_UPLOAD.replace(':id', id);
    postApiCall(url, { groupIds, selections }).then(onUploadSuccess).catch(onUploadError);
  };

  function onUploadSuccess(data) {
    const copied = Number(data?.totalCopied) || 0;
    const skipped = Number(data?.duplicateSkipped) || 0;
    toast.success(
      `${copied} contact${copied === 1 ? '' : 's'} imported` +
        (skipped ? `, ${skipped} duplicate${skipped === 1 ? '' : 's'} skipped` : ''),
    );
    setButtonDisable(false);
    setSelectedGroups([]);
    setPicks({});
    navigate('/contact-group');
  }

  function onUploadError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to upload contacts');
    }
  }

  const clearPick = (gid) =>
    setPicks((prev) => {
      const { [gid]: _drop, ...rest } = prev;
      return rest;
    });

  const handleSelectAll = () => {
    if (selectedGroups.length === groupsData.length) {
      setSelectedGroups([]);
      setPicks({});
    } else {
      setSelectedGroups(groupsData.map((group) => group.id));
    }
  };

  const handleCheckboxToggle = (userId) => {
    const isSelected = selectedGroups.includes(userId);
    if (isSelected) clearPick(userId);
    setSelectedGroups(
      isSelected ? selectedGroups.filter((id) => id !== userId) : [...selectedGroups, userId],
    );
  };

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      enableHiding: false,
      header: () => (
        <Checkbox
          checked={selectedGroups.length === groupsData.length && groupsData.length > 0}
          onCheckedChange={handleSelectAll}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedGroups.includes(row.original.id)}
          onCheckedChange={() => handleCheckboxToggle(row.original.id)}
          aria-label="Select row"
        />
      ),
    },
    { accessorKey: 'group_name', header: 'Group' },
    { accessorKey: 'member_count', header: 'Member' },
    {
      id: '_import',
      header: 'Import',
      enableSorting: false,
      cell: ({ row }) => {
        const g = row.original;
        if (!selectedGroups.includes(g.id)) return <span className="text-muted-foreground">-</span>;
        const ids = picks[g.id];
        return (
          <div className="flex items-center gap-2">
            <Select
              value={ids ? 'specific' : 'whole'}
              onValueChange={(v) => (v === 'whole' ? clearPick(g.id) : setPickerGroup(g))}
            >
              <SelectTrigger className="h-8 w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="whole">Whole group</SelectItem>
                <SelectItem value="specific">Specific contacts</SelectItem>
              </SelectContent>
            </Select>
            {ids && (
              <Button variant="outline" size="sm" className="h-8" onClick={() => setPickerGroup(g)}>
                {ids.length} selected
              </Button>
            )}
          </div>
        );
      },
    },
    { accessorFn: (row) => row.group_creator?.username, id: 'group_creator', header: 'Created by' },
    {
      accessorKey: 'created_at',
      header: 'Created on',
      cell: ({ row }) => new Date(row.original.created_at).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
      }),
    },
  ];
  if (!canImportContactGroupMembers()) {
    return <UploadNotPermitted title="Import Members" what="contact group member files" />;
  }

  return (
    <FillPage>
      <PageHeader
        title="Import Members"
        description="Copy a whole group, or picked contacts from it, into this one. Numbers already here are skipped. A group can hold at most 50,000 contacts; an import that would pass that is not copied."
      />
      <div className="contact-groups-container">
      <div className="row">
        <div className="col-12">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <div className="px-4 py-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                  <div className="input-group" style={{ width: '300px', maxWidth: '100%' }}>
                    <span className="input-group-text">
                      <FiSearch />
                    </span>
                    <input
                      className="form-control"
                      placeholder="Search groups..."
                      type="text"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="pb-4 px-4">
                <div style={{ overflowX: 'auto' }}>
                  <DataTable
                    tableId="contact-group.import"
                    columns={columns}
                    data={filteredGroups}
                    emptyTitle="No groups"
                    emptyDescription="No other contact groups available."
                    pageSize={10}
                    disableExport
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      {pickerGroup && (
        <MemberPickerDialog
          key={pickerGroup.id}
          group={pickerGroup}
          initial={picks[pickerGroup.id] || []}
          onClose={() => setPickerGroup(null)}
          onSave={(ids) => {
            setPicks((prev) => ({ ...prev, [pickerGroup.id]: ids }));
            setPickerGroup(null);
          }}
        />
      )}
      <Button className="mt-3" onClick={handleImportMembers} disabled={buttonDisable}>
        {buttonDisable ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Import Members'}
      </Button>
      </div>
    </FillPage>
  );
};

export default ContactGroupImport;
